import { useNavigate } from 'react-router-dom'
import {
  Trash2,
  Minus,
  Plus,
  ShoppingCart,
  ArrowRight,
  Tag,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useCart } from '@/contexts/CartContext'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Breadcrumb from '@/components/Breadcrumb'
import MetaTags from '@/components/MetaTags'
import { useState } from 'react'

const Cart = () => {
  const {
    items,
    removeItem,
    updateQuantity,
    getTotalPrice,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    getDiscount,
    getFinalPrice,
  } = useCart()
  const navigate = useNavigate()
  const [couponCode, setCouponCode] = useState('')

  const formatOrientation = (orientation: string) => {
    if (orientation === 'quadrado-grande') return 'Quadrado Grande'
    if (orientation === 'paisagem-grande') return 'Paisagem Grande'
    return orientation.charAt(0).toUpperCase() + orientation.slice(1)
  }

  const formatCoverType = (coverType?: string) => {
    if (!coverType) return null
    const coverLabels: Record<string, string> = {
      fotografica: 'Capa Fotográfica',
      linho: 'Capa de Linho',
      couro: 'Capa de Couro',
      'couro-premium': 'Capa de Couro',
      acrilico: 'Capa de Acrílico',
    }
    return coverLabels[coverType] || coverType
  }

  const handleApplyCoupon = () => {
    if (couponCode.trim()) {
      const success = applyCoupon(couponCode.trim())
      if (success) {
        setCouponCode('')
      }
    }
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <MetaTags
          title="Carrinho de Compras - FotoLivro Store"
          description="Revise seus fotolivros personalizados no carrinho de compras e finalize seu pedido com frete grátis para todo o Brasil."
          type="website"
        />
        <Header />
        <Breadcrumb />
        <main className="flex-1 container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <ShoppingCart className="h-24 w-24 text-muted-foreground mb-6" />
              <h1 className="text-3xl font-bold mb-4">
                Seu carrinho está vazio
              </h1>
              <p className="text-muted-foreground mb-8">
                Adicione produtos ao carrinho para continuar suas compras
              </p>
              <Button onClick={() => navigate('/')} size="lg">
                Ver Produtos
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title="Carrinho de Compras - FotoLivro Store"
        description="Revise seus fotolivros personalizados no carrinho de compras e finalize seu pedido com frete grátis para todo o Brasil."
        type="website"
      />
      <Header />
      <Breadcrumb />
      <main className="flex-1 bg-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-3xl font-bold mb-8">Carrinho de Compras</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map(item => (
                <Card key={item.id}>
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                      <div className="flex-1 space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-semibold">
                              {item.productName}
                            </h3>
                            <p className="text-muted-foreground mt-1">
                              {formatOrientation(item.orientation)} •{' '}
                              {item.pages} páginas
                              {item.coverType &&
                                ` • ${formatCoverType(item.coverType)}`}
                            </p>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeItem(item.id)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <div className="flex items-center gap-3">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-9 w-9"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-12 text-center font-semibold text-lg">
                              {item.quantity}
                            </span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-9 w-9"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">
                              R${' '}
                              {(item.price * item.quantity).toLocaleString(
                                'pt-BR',
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              R$ {item.price.toLocaleString('pt-BR')} cada
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                Continuar Comprando
              </Button>
            </div>

            <div className="lg:col-span-1">
              <Card className="lg:sticky lg:top-24">
                <CardContent className="p-4 md:p-6 space-y-4 md:space-y-6">
                  <h2 className="text-xl font-bold">Resumo do Pedido</h2>

                  <div className="space-y-3">
                    <div className="flex justify-between text-muted-foreground">
                      <span>
                        Subtotal (
                        {items.reduce((acc, item) => acc + item.quantity, 0)}{' '}
                        {items.reduce((acc, item) => acc + item.quantity, 0) ===
                        1
                          ? 'item'
                          : 'itens'}
                        )
                      </span>
                      <span>R$ {getTotalPrice().toLocaleString('pt-BR')}</span>
                    </div>

                    {appliedCoupon && (
                      <div className="flex justify-between text-green-600 font-semibold">
                        <span>Desconto ({appliedCoupon.code})</span>
                        <span>
                          - R$ {getDiscount().toLocaleString('pt-BR')}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between text-green-600 font-semibold">
                      <span>Frete</span>
                      <span>GRÁTIS</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span className="text-primary">
                        R$ {getFinalPrice().toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h3 className="font-semibold text-sm">Cupom de Desconto</h3>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-semibold text-green-700">
                            {appliedCoupon.code}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={removeCoupon}
                          className="h-8 text-green-700 hover:text-green-900"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Digite o cupom"
                          value={couponCode}
                          onChange={e =>
                            setCouponCode(e.target.value.toUpperCase())
                          }
                          onKeyDown={e =>
                            e.key === 'Enter' && handleApplyCoupon()
                          }
                        />
                        <Button onClick={handleApplyCoupon} variant="outline">
                          Aplicar
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-3 text-center">
                    <p className="text-sm font-semibold text-secondary">
                      🚚 Frete GRÁTIS para todo o Brasil
                    </p>
                  </div>

                  <Button
                    onClick={() => navigate('/checkout')}
                    size="lg"
                    className="w-full text-sm md:text-base py-4 md:py-6"
                  >
                    Finalizar Compra
                    <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Parcelamento em até 12x sem juros no cartão
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Cart
